import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import httpClient from '../../services/httpClient';
import { EyeIcon, EyeOffIcon } from '../Layout/icons';
import { Container, Form, Input, Button, Title, InfoText, FormGroup, Label, ErrorText, PasswordInputWrapper, TogglePasswordButton, Message } from './styles';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const activationCode = searchParams.get('activationCode');

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [status, setStatus] = useState(null); // null | 'success' | 'error'

    function handleChange(event) {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        
        // Limpa erro do campo quando usuário começa a digitar
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }
    }

    function validateForm() {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'A senha é obrigatória';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'A confirmação de senha é obrigatória';
        }

        if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'As senhas não coincidem';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!activationCode) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await httpClient.patch(
                `/usuarios/mudar-senha?activationCode=${activationCode}`,
                { password: formData.password }
            );
            
            if (response.ok) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (error) {
            setStatus('error');
        } finally {
            setIsLoading(false);
        }
    }

    // Se não tiver activationCode, mostra mensagem de erro
    if (!activationCode) {
        return (
            <Container>
                <Title>Link inválido</Title>
                <InfoText $error>
                    O link de redefinição de senha é inválido ou expirou. 
                    Por favor, solicite uma nova recuperação de senha.
                </InfoText>
                <Button type="button" onClick={() => navigate('/recuperar-senha')}>
                    Solicitar nova recuperação
                </Button>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/')}
                    style={{ marginTop: '12px' }}
                >
                    Voltar para o login
                </Button>
            </Container>
        );
    }

    // Tela de sucesso
    if (status === 'success') {
        return (
            <Container>
                <Title>Senha redefinida com sucesso!</Title>
                <Message>
                    Sua senha foi alterada com sucesso. 
                    Agora você pode fazer login com sua nova senha.
                </Message>
                <Button type="button" onClick={() => navigate('/')}>
                    Ir para o login
                </Button>
            </Container>
        );
    }

    // Tela de erro
    if (status === 'error') {
        return (
            <Container>
                <Title>Erro ao redefinir senha</Title>
                <Message $error>
                    Não foi possível redefinir sua senha. O link pode ter expirado 
                    ou já foi utilizado. Por favor, solicite uma nova recuperação de senha.
                </Message>
                <Button type="button" onClick={() => navigate('/recuperar-senha')}>
                    Solicitar nova recuperação
                </Button>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/')}
                    style={{ marginTop: '12px' }}
                >
                    Voltar para o login
                </Button>
            </Container>
        );
    }

    return (
        <Container>
            <Title>Redefinir senha</Title>
            <InfoText>
                Defina sua nova senha de acesso. Certifique-se de escolher uma senha segura.
            </InfoText>
            <Form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label htmlFor="password">Nova senha *</Label>
                    <PasswordInputWrapper>
                        <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Digite sua nova senha"
                            value={formData.password}
                            onChange={handleChange}
                            $hasError={!!errors.password}
                            required
                        />
                        <TogglePasswordButton
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </TogglePasswordButton>
                    </PasswordInputWrapper>
                    {errors.password && <ErrorText>{errors.password}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="confirmPassword">Confirmar nova senha *</Label>
                    <PasswordInputWrapper>
                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Digite novamente sua senha"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            $hasError={!!errors.confirmPassword}
                            required
                        />
                        <TogglePasswordButton
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                        >
                            {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </TogglePasswordButton>
                    </PasswordInputWrapper>
                    {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
                </FormGroup>

                <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Processando...' : 'Redefinir senha'}
                </Button>
            </Form>
        </Container>
    );
}
